import axios from "axios";
import * as Sentry from "@sentry/react";

// ---------------------------------------------------------------------------
// Client for the iventas-analytics microservice (ClickHouse/MongoDB backed
// analytics API). This is a separate backend from the main iventas REST API,
// so it gets its own axios instance and base URL.
// ---------------------------------------------------------------------------
export const ANALYTICS_API = axios.create({
  baseURL: process.env.REACT_APP_ANALYTICS_API_URL,
  timeout: 30000,
});

ANALYTICS_API.interceptors.request.use((config) => {
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
  return config;
});

ANALYTICS_API.interceptors.response.use(
  (response) => response,
  (error) => {
    const jcnError = {
      message:
        error.response?.data?.message ||
        error.response?.data?.userMessage ||
        error.message,
      httpCode: error.response?.status,
    };

    // Skip Sentry for expected-flow client errors (4xx validation errors).
    // Auth errors (401/403) are reported but handled locally by each widget —
    // they must NOT clear localStorage or redirect globally because the main
    // app session is still valid; only the analytics microservice token scope
    // may be limited (e.g. insufficient company permissions).
    const isExpectedFlowError = jcnError.httpCode >= 400 && jcnError.httpCode < 500;
    if (!isExpectedFlowError) {
      Sentry.captureException(error, {
        tags: { component: "analyticsDashboard.service", op: "responseInterceptor" },
      });
    }

    // Do NOT call localStorage.clear() or window.location.replace() here.
    // Auth errors from the analytics microservice are isolated to analytics
    // widgets and must not terminate the user's main dashboard session.

    return Promise.reject(jcnError);
  }
);

const buildParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, Array.isArray(value) ? value.join(",") : value.toString());
    }
  });
  return searchParams.toString();
};

export const analyticsDashboardApi = {
  // ClickHouse Raw Data Endpoints -------------------------------------------------
  getClickHouseMessageAnalytics: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/message-analytics?${buildParams(params)}`);
    return response.data;
  },

  // Returns message volume grouped by hour-of-day (0-23, UTC).
  // Response shape: { success, data: [{ hour_utc, total_messages, incoming_messages, outgoing_messages }], utcOffsetHint }
  getClickHouseMessageAnalyticsByHour: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/message-analytics-by-hour?${buildParams(params)}`);
    return response.data;
  },

  getClickHouseUserAnalytics: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/user-analytics?${buildParams(params)}`);
    return response.data;
  },

  getClickHouseClientAnalytics: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/client-analytics?${buildParams(params)}`);
    return response.data;
  },

  getClickHouseMaterializedView: async (viewName, params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/view/${viewName}?${buildParams(params)}`);
    return response.data;
  },

  // SECURITY: `executeClickHouseQuery` (arbitrary client-supplied SQL sent to
  // the now-removed `POST /analytics/clickhouse/query` endpoint) has been
  // removed. Use the fixed, parameterized, company-scoped endpoints below
  // instead (see PR #168 follow-up: iVentas-Plus/iventas-analytics).
  getChannelPerformance: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/channel-performance?${buildParams(params)}`);
    return response.data;
  },

  getSourceDetails: async (params) => {
    const response = await ANALYTICS_API.get(`/analytics/clickhouse/source-details?${buildParams(params)}`);
    return response.data;
  },

  // Data enrichment ------------------------------------------------------------
  getUsers: async (companyId) => {
    const response = await ANALYTICS_API.get(`/users/company/${companyId}`);
    return response.data;
  },

  getLeadGenForms: async (companyId) => {
    const response = await ANALYTICS_API.get(`/leadgen/forms/${companyId}`);
    return response.data;
  },

  getActiveChannels: async (companyId) => {
    const response = await ANALYTICS_API.get(`/channels/company/${companyId}/active`);
    return response.data;
  },

  // Client Dashboard Analytics --------------------------------------------------
  getCampaignEffectiveness: async (query) => {
    const response = await ANALYTICS_API.get(`/analytics/dashboard/campaign-effectiveness?${buildParams(query)}`);
    return response.data;
  },

  getClientSourceAnalysis: async (query) => {
    const response = await ANALYTICS_API.get(`/analytics/dashboard/client-source-analysis?${buildParams(query)}`);
    return response.data;
  },

  getFacebookFormPerformance: async (query) => {
    const response = await ANALYTICS_API.get(`/analytics/dashboard/facebook-form-performance?${buildParams(query)}`);
    return response.data;
  },

  getCampaigns: async (query) => {
    const response = await ANALYTICS_API.get(`/campaigns?${buildParams(query)}`);
    return response.data;
  },

  getAgentPerformanceBySource: async (params) => {
    const searchParams = buildParams({
      companyId: params.companyId,
      startDate: params.startDate,
      endDate: params.endDate,
      channelIds: params.channelId,
      source: params.source,
      sourceId: params.sourceId,
    });
    const response = await ANALYTICS_API.get(`/analytics/dashboard/agent-performance-by-source?${searchParams}`);
    return response.data;
  },

  getAvailableLeadSources: async (params) => {
    const searchParams = buildParams({
      companyId: params.companyId,
      startDate: params.startDate,
      endDate: params.endDate,
      channelIds: params.channelId,
    });
    const response = await ANALYTICS_API.get(`/analytics/dashboard/available-lead-sources?${searchParams}`);
    return response.data;
  },

  getAssignedConversations: async (params) => {
    const searchParams = buildParams({
      companyId: params.companyId,
      startDate: params.startDate,
      endDate: params.endDate,
      channelIds: params.channelId,
    });
    const response = await ANALYTICS_API.get(`/analytics/dashboard/assigned-conversations?${searchParams}`);
    return response.data;
  },

  /**
   * Fetches client rows for the "Lista de Conversaciones" sheet of the
   * Analytics Excel export. Maps to GET /analytics/export-report/clients.
   *
   * @param {{ companyId: string, startDate: string, endDate: string, channelId?: string, limit?: number }} params
   * @returns {Promise<{ success: boolean, data: object[], metadata: object }>}
   */
  getExportReportClients: async (params) => {
    const searchParams = buildParams({
      companyId: params.companyId,
      startDate: params.startDate,
      endDate: params.endDate,
      channelId: params.channelId,
      // timezoneOffset: minutes offset of the user's local time vs UTC.
      // E.g. UTC-6 (Mexico City) → -360 → backend shifts dates by -6h so that
      // "2026-07-01" means local midnight instead of UTC midnight.
      timezoneOffset: params.timezoneOffset,
      limit: params.limit,
    });
    const response = await ANALYTICS_API.get(`/analytics/export-report/clients?${searchParams}`);
    return response.data;
  },
};

export default analyticsDashboardApi;
